import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { MetaTags } from '../../components/seo/MetaTags';

export function Terms() {
  return (
    <>
      <MetaTags
        title="Terms of Service"
        description="Terms and conditions for using Plant Identifier, including payment terms and service usage guidelines."
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-4"
        >
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-2 bg-emerald-100 rounded-full mb-4">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-600 mb-4">
                Welcome to Plant Identifier. By accessing or using our service,
                you agree to be bound by these terms. Please read these terms
                carefully before using our service.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. Definitions
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>"Service" refers to the Plant Identifier application</li>
                <li>
                  "User" refers to any individual accessing or using the Service
                </li>
                <li>
                  "Credits" refer to the in-app currency used for plant
                  identifications
                </li>
                <li>"Subscription" refers to any recurring payment plan</li>
              </ul>
            </section>

            {/* Account Terms */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. Account Terms
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  You must provide accurate and complete information when
                  creating an account. You are responsible for maintaining the
                  security of your account credentials.
                </p>
                <p>
                  We reserve the right to suspend or terminate accounts that
                  violate these terms or are inactive for an extended period.
                </p>
              </div>
            </section>

            {/* Payment Terms */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. Payment and Billing
              </h2>
              <div className="space-y-4 text-gray-600">
                <h3 className="font-medium text-gray-800">
                  4.1 Payment Processing
                </h3>
                <p>
                  Payments are processed securely through Razorpay. By making a
                  payment, you agree to Razorpay's terms of service and
                  authorize us to charge your chosen payment method.
                </p>

                <h3 className="font-medium text-gray-800">
                  4.2 Credit Purchases
                </h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Credits are non-refundable and non-transferable</li>
                  <li>Credits expire at the end of each calendar month</li>
                  <li>Unused credits do not roll over to the next month</li>
                  <li>Credit prices are subject to change with notice</li>
                </ul>

                <h3 className="font-medium text-gray-800">4.3 Refund Policy</h3>
                <p>
                  Refunds are processed within 7 days of purchase if requested.
                  No refunds will be issued for partially used credits or after
                  the 7-day period.
                </p>

                <h3 className="font-medium text-gray-800">
                  4.4 Payment Security
                </h3>
                <p>
                  All payments are encrypted and processed securely. We never
                  store your complete credit card information on our servers.
                </p>

                <h3 className="font-medium text-gray-800">
                  4.5 Failed Payments
                </h3>
                <p>
                  In case of payment failure, the transaction will be cancelled
                  and no credits will be issued until successful payment is
                  received.
                </p>
              </div>
            </section>

            {/* Service Usage */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Service Usage
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  The Service is provided "as is" without warranties of any
                  kind. We do not guarantee 100% accuracy in plant
                  identification.
                </p>
                <p>
                  Users are responsible for verifying plant information,
                  especially regarding edibility or toxicity.
                </p>
              </div>
            </section>

            {/* Limitations */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. Limitations and Restrictions
              </h2>
              <div className="space-y-4 text-gray-600">
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    You may not use automated methods to access the Service
                  </li>
                  <li>You may not attempt to bypass the credit system</li>
                  <li>You may not share or resell your account access</li>
                  <li>
                    We reserve the right to limit identification requests per
                    user
                  </li>
                </ul>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Changes to Terms
              </h2>
              <p className="text-gray-600">
                We reserve the right to modify these terms at any time. Users
                will be notified of significant changes via email or in-app
                notifications.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Contact Information
              </h2>
              <p className="text-gray-600">
                For questions about these terms, please contact us.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </>
  );
}
